'use client'
import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useId, useRef, useState } from 'react'
import { cx } from '../utils'

export function DropdownMenu({
  trigger,
  items,
  align = 'right',
}: {
  trigger: ReactNode
  align?: 'left' | 'right'
  items: Array<{ label: string; onSelect: () => void; tone?: 'danger'; divide?: boolean }>
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', esc)
    }
  }, [open])
  return (
    <div ref={ref} className="relative">
      <span onClick={() => setOpen((v) => !v)}>{trigger}</span>
      {open && (
        <div
          role="menu"
          style={{ zIndex: 9999 }}
          className={cx(
            'absolute top-full mt-1.5 min-w-[156px] rounded-sm border border-line-strong bg-surface py-1 shadow-[var(--shadow-md)]',
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          {items.map((item) => (
            <button
              key={item.label}
              role="menuitem"
              onClick={() => { setOpen(false); item.onSelect() }}
              className={cx(
                'block w-full px-3.5 py-2 text-left text-[15px] whitespace-nowrap transition-colors duration-100 hover:bg-accent-soft',
                item.divide && 'mt-1 border-t border-line pt-2',
                item.tone === 'danger' ? 'text-danger' : 'text-ink-2 hover:text-ink',
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
